import { useState } from 'react'
import { MAX_PROGRESS } from '../constants'

const BUTTON_LABELS = ['Kick Me', 'Push Me', 'Slap Me', 'Punch Me', 'Abuse Me']
const MIN_SHUFFLE_CLICKS = 3
const MAX_SHUFFLE_CLICKS = 8

const randomShuffleCount = () =>
  Math.floor(Math.random() * (MAX_SHUFFLE_CLICKS - MIN_SHUFFLE_CLICKS + 1)) + MIN_SHUFFLE_CLICKS

const shuffleOrder = (list) => {
  const next = [...list]
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[next[i], next[j]] = [next[j], next[i]]
  }
  return next
}

const createButtons = () =>
  BUTTON_LABELS.map((label, index) => ({
    id: `button-${index + 1}`,
    label,
    progress: 0,
    totalClicks: 0,
    nextShuffleAt: randomShuffleCount(),
  }))

function WeirdButtons() {
  const [game, setGame] = useState(() => ({
    buttons: createButtons(),
    activeId: null,
  }))

  const handleClick = (id) => {
    setGame((prev) => {
      const { buttons, activeId } = prev
      const clickedIndex = buttons.findIndex((button) => button.id === id)
      if (clickedIndex === -1) {
        return prev
      }

      const activeButton = activeId
        ? buttons.find((button) => button.id === activeId)
        : null
      const activeIncomplete = activeButton ? activeButton.progress < MAX_PROGRESS : false

      let nextButtons = buttons
      let nextActiveId = activeId

      if (!activeId) {
        nextActiveId = id
      } else if (activeId !== id && activeIncomplete) {
        nextButtons = buttons.map((button) => ({
          ...button,
          progress: 0,
          totalClicks: 0,
          nextShuffleAt: randomShuffleCount(),
        }))
        nextActiveId = id
      } else if (activeId !== id) {
        nextActiveId = id
      }

      const updatedIndex = nextButtons.findIndex((button) => button.id === id)
      const clickedButton = nextButtons[updatedIndex]
      if (!clickedButton) {
        return { buttons: nextButtons, activeId: nextActiveId }
      }

      const nextProgress = Math.min(MAX_PROGRESS, clickedButton.progress + 1)
      const nextTotalClicks = clickedButton.totalClicks + 1
      const shouldShuffle = nextTotalClicks >= clickedButton.nextShuffleAt

      let updated = nextButtons.map((button, position) => {
        if (position !== updatedIndex) {
          return button
        }

        return {
          ...button,
          progress: nextProgress,
          totalClicks: nextTotalClicks,
          nextShuffleAt: shouldShuffle
            ? nextTotalClicks + randomShuffleCount()
            : button.nextShuffleAt,
        }
      })

      if (shouldShuffle) {
        updated = shuffleOrder(updated)
      }

      return { buttons: updated, activeId: nextActiveId }
    })
  }

  return (
    <section className="weird-page" aria-label="Weird buttons">
      <div>
        <p className="eyebrow">Playground</p>
        <h1>Weird buttons</h1>
        <p className="subtitle">
          Pick any button to start the game. Every click adds exactly 1% to the progress bar of
          the button you clicked. Keep clicking the same button until its bar reaches 100%. If you
          click a different button while the current one is still below 100%, all progress resets
          to zero and the game restarts with the newly clicked button as the active one. After a
          random number of clicks, the buttons will reshuffle along with their progress, so pay
          attention to the new order.
        </p>
      </div>
      <div className="weird-list" role="list">
        {game.buttons.map((button) => {
          const isComplete = button.progress >= MAX_PROGRESS

          return (
            <div
              className={`weird-row ${isComplete ? 'is-complete' : ''}`}
              role="listitem"
              key={button.id}
            >
              <button
                className="weird-button"
                type="button"
                onClick={() => handleClick(button.id)}
              >
                <span>{button.label}</span>
              </button>
              <div
                className="progress"
                role="progressbar"
                aria-label={`Progress ${button.label}`}
                aria-valuemin={0}
                aria-valuemax={MAX_PROGRESS}
                aria-valuenow={Math.round(button.progress)}
              >
                <div className="progress-fill" style={{ width: `${button.progress}%` }} />
              </div>
            </div>
          )
        })}
      </div>
      {game.buttons.every((button) => button.progress >= MAX_PROGRESS) ? (
        <div className="weird-success" role="status">
          Congratulations! You completed all the buttons.
        </div>
      ) : null}
      <a className="back-link" href="/">
        Back to playground
      </a>
    </section>
  )
}

export default WeirdButtons
